import { ActiveElement, FontSpec, TooltipCallbacks } from 'chart.js'
import { AnnotationOptions } from 'chartjs-plugin-annotation'
import { format, startOfDay } from 'date-fns'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { useTranslation } from 'react-i18next'

import { Box, Grid, useTheme } from '@mui/material'

import { TrackingEventTimelineItem } from '../../features/live-events/types/LiveEvents'
import { TrackedGame } from '../../features/live-events/types/TrackedGame'
import { resolveTimeunitByGranularity } from '../../features/revenue-and-downloads/helpers/helpers'
import { GranularityValue } from '../../features/revenue-and-downloads/types/Filters'
import { PerformanceChartColor } from '../../features/update-history/components/PerformanceChart/PerformanceChart'
import { useGameUpdatePerformanceData } from '../../features/update-history/hooks/useGameUpdatePerformanceData'
import { ChartDataFormat } from '../../features/update-history/types/types'
import { fillMissingBucketsWithZero } from '../../helpers/fillMissingBucketsWithZero'
import { valueIsBetween } from '../../helpers/valueIsBetween'
import { useExportDataPerformanceChartV2 } from '../../hooks/exportDataHooks'
import { useError } from '../../hooks/useError'
import { useDataExport } from '../../hooks/useExportData'
import { DateRangeValue } from '../DateRangePicker/DateRangePicker'
import type { TimelineChartVerticalMark } from '../GamePerformanceDialog/GamePerformanceDialog'
import { NewEventsTimelineStrip } from '../NewEventsTimelineStrip/NewEventsTimelineStrip'
import { OverlayLoader } from '../OverlayLoader/OverlayLoader'
import { ScaleConfig, TimeScaleConfig, TimelineChart, TimelineChartLayoutInfo, TimelineChartMark } from '../TimelineChart/TimelineChart'
import { PerformanceChartV2DataType } from './PerformanceChartV2DataType'
import { PerformanceChartV2Filters } from './PerformanceChartV2Filters'
import { YAxisConfig } from './PerformanceChartV2Types'
import { getMinDateForDataType } from './getMinDateForDataType'

type PerformanceChartV2Props<VerticalMarkDataType extends TimelineChartVerticalMark<any>> = {
  appId: number
  marketIso: string
  dateRange?: DateRangeValue
  onDateRangeChanged?: (value?: DateRangeValue) => void
  granularity?: GranularityValue
  onGranularityChanged?: (value: GranularityValue) => void
  yAxisLeftConfig: YAxisConfig
  onYAxisLeftConfigChanged: (value: YAxisConfig) => void
  yAxisRightConfig: YAxisConfig
  onYAxisRightConfigChanged: (value: YAxisConfig) => void
  verticalMarks?: VerticalMarkDataType[]
  highlightedVerticalMarks?: VerticalMarkDataType[]
  selectedVerticalMarks?: VerticalMarkDataType[]
  highlightedPeriod?: { start: number; end: number }
  onChartClick?: (xValue: number | undefined, yElements: ActiveElement[]) => void
  onMarkClicked?: (value: any) => void
  children?: ReactNode
  selectedGame?: TrackedGame | undefined
  newEvents?: TrackingEventTimelineItem[]
  newEventsLoading?: boolean
}

const STRIP_HEIGHT = 50
const STRIP_TICKS_PADDING_DEFAULT = 40
const STRIP_TICKS_PADDING_DAY = 30

// data types are mapped to rolling days which affects calculations
const rollingDaysByDataType = {
  [PerformanceChartV2DataType.Revenue]: 1,
  [PerformanceChartV2DataType.Downloads]: 1,
  [PerformanceChartV2DataType.RevenueDownloadsRatio7Days]: 7,
  [PerformanceChartV2DataType.RevenueDownloadsRatio30Days]: 30,
  [PerformanceChartV2DataType.RevenueDownloadsRatio90Days]: 90,
  [PerformanceChartV2DataType.RevenueDownloadsRatioAllTime]: 0,
  [PerformanceChartV2DataType.Ranks]: 1,
  [PerformanceChartV2DataType.DAU]: 1,
  [PerformanceChartV2DataType.MAU]: 1,
  [PerformanceChartV2DataType.ARPDAU]: 1,
}

export const PerformanceChartV2 = <VerticalMarkDataType extends TimelineChartVerticalMark<any>>({
  appId,
  marketIso,
  dateRange,
  granularity = GranularityValue.Day,
  onGranularityChanged,
  onDateRangeChanged,
  verticalMarks,
  highlightedVerticalMarks,
  selectedVerticalMarks,
  highlightedPeriod,
  yAxisLeftConfig,
  onYAxisLeftConfigChanged,
  yAxisRightConfig,
  onYAxisRightConfigChanged,
  onChartClick,
  onMarkClicked,
  selectedGame,
  newEvents,
  newEventsLoading,
}: PerformanceChartV2Props<VerticalMarkDataType>) => {
  const chartRef = useRef<ChartJSOrUndefined<'line'>>()
  const [chartAreaBounds, setChartAreaBounds] = useState<
    { left: number; right: number; containerWidth: number; bottom: number; xMin: number; xMax: number } | undefined
  >()
  const leftYAxisData = useGameUpdatePerformanceData({
    appId,
    gameId: selectedGame?.game.id,
    marketIso,
    rollingDays: rollingDaysByDataType[yAxisLeftConfig.dataType],
    granularity,
  })

  const rightYAxisData = useGameUpdatePerformanceData({
    appId,
    gameId: selectedGame?.game.id,
    marketIso,
    rollingDays: rollingDaysByDataType[yAxisRightConfig.dataType],
    granularity,
  })

  const exportableData = useGameUpdatePerformanceData({
    appId,
    gameId: selectedGame?.game.id,
    marketIso,
    rollingDays: 1,
    granularity,
  })

  const error = leftYAxisData.error || rightYAxisData.error || exportableData.error
  useError({ error })

  const excludedDataTypes = [...(yAxisLeftConfig?.excludedDataTypes || []), ...(yAxisRightConfig?.excludedDataTypes || [])].filter(
    (value, index, self) => self.indexOf(value) === index
  )

  const { handleExportRequested, handleExport, exportRequested } = useDataExport()

  const exportData = useExportDataPerformanceChartV2(
    { appId, marketIso, dateRange, granularity, excludedDataTypes, data: exportableData },
    { skip: !exportRequested }
  )

  useEffect(() => {
    if (exportData && exportRequested) {
      handleExport({
        ...exportData,
        sheetName: 'Game Performance',
      })
    }
  }, [exportData, exportRequested, handleExport])

  const chartConfig = useYAxisConfig({
    axisConfigs: [
      { data: leftYAxisData, axisConfig: yAxisLeftConfig },
      { data: rightYAxisData, axisConfig: yAxisRightConfig },
    ],
    dateRange,
    granularity,
  })

  const tooltipCallbacks = useMemo(() => {
    return {
      title: (tooltipItems) => {
        switch (granularity) {
          case GranularityValue.Day:
            return format(tooltipItems[0].parsed.x, 'EEE, LLL d, yyyy')
          case GranularityValue.Week:
            return format(tooltipItems[0].parsed.x, 'I/RRRR')
          case GranularityValue.Month:
            return format(tooltipItems[0].parsed.x, 'MMM, yyyy')
        }
      },
    } as Partial<TooltipCallbacks<'line'>>
  }, [granularity])

  // Use the earliest date from the actually selected y-axis data series so
  // the date-range picker minDate (and the "All Available Data" preset)
  // line up with the first date the chart can render for the current
  // selection, instead of the global minimum across every series.
  const minDate = useMemo(() => {
    const left = getMinDateForDataType(leftYAxisData, yAxisLeftConfig.dataType)
    const right = getMinDateForDataType(rightYAxisData, yAxisRightConfig.dataType)
    return Math.min(left, right)
  }, [leftYAxisData, rightYAxisData, yAxisLeftConfig.dataType, yAxisRightConfig.dataType])

  const filteredVerticalMarks = useMemo(() => verticalMarks?.map(markMapper).filter(markFilter(dateRange)), [dateRange, verticalMarks])
  const filteredHighlightedVerticalMarks = useMemo(
    () => highlightedVerticalMarks?.map(markMapper).filter(markFilter(dateRange)),
    [dateRange, highlightedVerticalMarks]
  )
  const filteredSelectedVerticalMarks = useMemo(() => selectedVerticalMarks?.map(markMapper).filter(markFilter(dateRange)), [dateRange, selectedVerticalMarks])

  const { t } = useTranslation()
  const theme = useTheme()

  const trackingStartAnnotation = useMemo<AnnotationOptions[]>(() => {
    const rawTs = selectedGame?.trackingStarted
    if (rawTs == null) return []
    const ts = startOfDay(new Date(rawTs)).getTime()
    const from = dateRange?.fromDate?.getTime()
    const to = dateRange?.toDate?.getTime()
    if ((from != null && ts < from) || (to != null && ts > to)) return []
    const color = theme.palette.success.main
    const annotation: AnnotationOptions<'line'> = {
      type: 'line',
      scaleID: 'x',
      value: ts,
      borderColor: color,
      borderWidth: 2,
      label: {
        content: t('live-events:tracking_started'),
        enabled: true,
        position: 'start',
        backgroundColor: color,
        // chartjs-plugin-annotation types `font` as full FontSpec, but Chart.js merges with defaults at runtime — only `size` is needed here.
        font: { size: 11 } as unknown as FontSpec,
        padding: 4,
      },
    }
    return [annotation]
  }, [selectedGame?.trackingStarted, dateRange, theme.palette.success.main, t])

  const isLoading = leftYAxisData.isLoading || rightYAxisData.isLoading

  // Driven by Chart.js's `afterUpdate` plugin hook via `onLayoutUpdate`
  const handleLayoutUpdate = useCallback((info: TimelineChartLayoutInfo) => {
    setChartAreaBounds((prev) => {
      if (
        prev &&
        prev.left === info.chartAreaLeft &&
        prev.right === info.chartAreaRight &&
        prev.containerWidth === info.containerWidth &&
        prev.bottom === info.chartAreaBottom &&
        prev.xMin === info.xMin &&
        prev.xMax === info.xMax
      ) {
        return prev
      }
      return {
        left: info.chartAreaLeft,
        right: info.chartAreaRight,
        containerWidth: info.containerWidth,
        bottom: info.chartAreaBottom,
        xMin: info.xMin,
        xMax: info.xMax,
      }
    })
  }, [])

  return (
    <Grid container wrap="wrap" spacing={2}>
      <Grid item xs={12}>
        <PerformanceChartV2Filters
          yAxisLeftConfig={yAxisLeftConfig}
          yAxisRightConfig={yAxisRightConfig}
          dateRange={dateRange}
          granularity={granularity}
          onGranularityChanged={onGranularityChanged}
          onYAxisLeftConfigChanged={onYAxisLeftConfigChanged}
          onYAxisRightConfigChanged={onYAxisRightConfigChanged}
          onDateRangeChanged={onDateRangeChanged}
          onExportFormatSelected={handleExportRequested}
          config={{ minDate }}
          isLoading={isLoading}
          marketIso={marketIso}
        />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ position: 'relative' }}>
          <OverlayLoader isLoading={isLoading}>
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '2 / 1' }}>
              <TimelineChart
                ref={chartRef}
                chartLineOptions={{
                  responsive: true,
                  // CSS `aspect-ratio` on the wrapping Box preserves the 2:1
                  // chart ratio without triggering the Chart.js v4
                  // "shrink-but-don't-grow-back" bug (issue #12177). (GR-2926)
                  maintainAspectRatio: false,
                  animation: false,
                }}
                data={{
                  datasets: chartConfig?.datasets?.map((d, i) => {
                    const chart = chartRef.current
                    if (!chart || !chart.data?.datasets?.[i]) return d
                    return { ...d, hidden: !chart.isDatasetVisible(i) }
                  }),
                }}
                scaleConfig={
                  newEvents
                    ? {
                        ...chartConfig.scaleConfig,
                        x: {
                          ...chartConfig.scaleConfig?.x,
                          ticks: {
                            padding: granularity === GranularityValue.Day ? STRIP_TICKS_PADDING_DAY : STRIP_TICKS_PADDING_DEFAULT,
                          },
                        } as TimeScaleConfig,
                      }
                    : chartConfig.scaleConfig
                }
                tooltipCallbacks={tooltipCallbacks}
                verticalMarks={filteredVerticalMarks}
                highlightedVerticalMarks={filteredHighlightedVerticalMarks}
                selectedVerticalMarks={filteredSelectedVerticalMarks}
                highlightedPeriod={highlightedPeriod}
                visibleDateRange={dateRange?.fromDate && dateRange?.toDate ? { fromDate: dateRange.fromDate, toDate: dateRange.toDate } : undefined}
                onChartClick={onChartClick}
                onVerticalMarkClicked={onMarkClicked}
                staticAnnotations={trackingStartAnnotation}
                onLayoutUpdate={handleLayoutUpdate}
              />
            </Box>
          </OverlayLoader>
          {newEvents && chartAreaBounds && (
            <Box sx={{ position: 'absolute', top: chartAreaBounds.bottom, left: 0, right: 0, height: STRIP_HEIGHT, pointerEvents: 'none' }}>
              <NewEventsTimelineStrip
                events={newEvents}
                dateRange={dateRange}
                chartAreaLeft={chartAreaBounds.left}
                chartAreaRight={chartAreaBounds.right}
                containerWidth={chartAreaBounds.containerWidth}
                xScaleMin={chartAreaBounds.xMin}
                xScaleMax={chartAreaBounds.xMax}
                isLoading={isLoading || !!newEventsLoading}
              />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

const markMapper = <T extends TimelineChartVerticalMark<any>>(mark: T): TimelineChartMark<T> => ({
  position: +mark.timestamp,
  data: mark.data,
})
const markFilter =
  <T extends TimelineChartVerticalMark<any>>(dateRange: DateRangeValue | undefined) =>
  (mark: TimelineChartMark<T>) => {
    const fromTime = dateRange?.fromDate?.getTime()
    let toTime = dateRange?.toDate?.getTime()

    // Normalize toTime to fix timezone issue
    if (toTime) {
      const toDate = new Date(toTime)
      toTime = new Date(toDate.getUTCFullYear(), toDate.getUTCMonth(), toDate.getUTCDate() - 1, 23, 59, 59, 999).getTime()
    }

    return valueIsBetween(mark.position, fromTime, toTime)
  }

type ResolveYAxisConfigParams = {
  axisConfigs: { data: ReturnType<typeof useGameUpdatePerformanceData>; axisConfig: YAxisConfig }[]

  dateRange?: DateRangeValue
  granularity: GranularityValue
}

const useYAxisConfig = ({ axisConfigs, dateRange, granularity }: ResolveYAxisConfigParams) => {
  const { t } = useTranslation()
  const averageLabel = () => (granularity !== GranularityValue.Day ? `(${t('common:average_label')})` : '')
  const spanGapsByGranularity = (granularity: GranularityValue) => {
    switch (granularity) {
      case GranularityValue.Day:
        return 90000000
      case GranularityValue.Week:
        return 691200000
      case GranularityValue.Month:
        return 2764800000
    }
  }

  return {
    datasets: axisConfigs.flatMap((axisConfig, index) => {
      const {
        data,
        axisConfig: { dataType },
      } = axisConfig
      const rollingDays = rollingDaysByDataType[dataType]
      const axisId = `y${index + 1}`
      const color = index === 0 ? PerformanceChartColor.Primary : PerformanceChartColor.Secondary

      switch (dataType) {
        case PerformanceChartV2DataType.Revenue:
          return resolveDataset(
            axisId,
            `${t('common:revenue_text')} (${t('common:apple_ios')})`,
            color,
            ChartDataFormat.Currency,
            fillMissingBucketsWithZero(
              data.estimates?.map((estimate) => ({
                x: estimate.ts,
                y: estimate.revenue || 0,
              })),
              granularity
            ),
            dateRange
          )
        case PerformanceChartV2DataType.Downloads:
          return resolveDataset(
            axisId,
            `${t('common:downloads_text')} (${t('common:apple_ios')})`,
            color,
            ChartDataFormat.Number,
            fillMissingBucketsWithZero(
              data.estimates?.map((estimate) => ({
                x: estimate.ts,
                y: estimate.downloads || 0,
              })),
              granularity
            ),
            dateRange
          )
        case PerformanceChartV2DataType.RevenueDownloadsRatio7Days:
        case PerformanceChartV2DataType.RevenueDownloadsRatio30Days:
        case PerformanceChartV2DataType.RevenueDownloadsRatio90Days:
          return resolveDataset(
            axisId,
            `${t('common:revenue_downloads_ratio_days', { days: rollingDays })} (${t('common:apple_ios')}) ${averageLabel()}`,
            color,
            ChartDataFormat.Currency,
            data.estimates?.map((estimate) => ({
              x: estimate.ts,
              y: estimate.revenueAndDownloadsRatio || 0,
            })),
            dateRange
          )
        case PerformanceChartV2DataType.RevenueDownloadsRatioAllTime:
          return resolveDataset(
            axisId,
            `${t('common:all_time_revenue_downloads_ratio')} (${t('common:apple_ios')}) ${averageLabel()}`,
            color,
            ChartDataFormat.Currency,
            data.estimates?.map((estimate) => ({
              x: estimate.ts,
              y: estimate.revenueAndDownloadsRatio || 0,
            })),
            dateRange
          )
        case PerformanceChartV2DataType.Ranks:
          return [
            resolveDataset(
              axisId,
              `${t('common:top_grossing_rank')} ${averageLabel()}`,
              color,
              ChartDataFormat.Number,
              data.topGrossingRanks?.map((rank) => ({
                x: rank.ts,
                y: Math.round(rank.rank),
              })),
              dateRange,
              spanGapsByGranularity(granularity)
            ),
            resolveDataset(
              axisId,
              `${t('common:free_rank')} ${averageLabel()}`,
              PerformanceChartColor.Tertiary,
              ChartDataFormat.Number,
              data.freeRanks?.map((rank) => ({
                x: rank.ts,
                y: Math.round(rank.rank),
              })),
              dateRange
            ),
          ]
        case PerformanceChartV2DataType.DAU:
          return resolveDataset(
            axisId,
            `${t('common:dau')} ${averageLabel()}`,
            color,
            ChartDataFormat.Number,
            data.dailyActiveUsers?.map((dau) => ({
              x: dau.date,
              y: Math.round(dau.activeUsers || 0),
            })),
            dateRange
          )
        case PerformanceChartV2DataType.MAU:
          return {
            ...resolveDataset(
              axisId,
              t('common:mau'),
              color,
              ChartDataFormat.Number,
              data.monthlyActiveUsers?.map((mau) => ({
                x: mau.date,
                y: Math.round(mau.activeUsers || 0),
              })),
              dateRange
            ),
            pointRadius: granularity !== GranularityValue.Month ? 3 : 0,
          }
        case PerformanceChartV2DataType.ARPDAU:
          return {
            ...resolveDataset(
              axisId,
              `${t('live-events:average_revenue_per_daily_active_user')} ${averageLabel()}`,
              color,
              ChartDataFormat.Currency,
              data.averageRevenuePerDailyActiveUser?.map((arpdau) => ({
                x: arpdau.date,
                y: Math.round(arpdau.value * 100) / 100,
              })),
              dateRange
            ),
          }
        default:
          return []
      }
    }),
    scaleConfig: {
      x: {
        time: {
          unit: resolveTimeunitByGranularity(granularity),
          isoWeekday: true,
        },
      } as TimeScaleConfig,
      ...axisConfigs.reduce((acc, axisConfig, index) => {
        const {
          data,
          axisConfig: { dataType },
        } = axisConfig
        const axisId = `y${index + 1}`
        const rollingDays = rollingDaysByDataType[dataType]
        switch (dataType) {
          case PerformanceChartV2DataType.Revenue:
            acc[axisId] = {
              dataFormat: ChartDataFormat.Currency,
              title: `${t('common:revenue_text')} (${t('common:apple_ios')})`,
              shorten: true,
            }

            return acc
          case PerformanceChartV2DataType.Downloads:
            acc[axisId] = {
              dataFormat: ChartDataFormat.Number,
              title: `${t('common:downloads_text')} (${t('common:apple_ios')})`,
              shorten: true,
            }

            return acc
          case PerformanceChartV2DataType.RevenueDownloadsRatio7Days:
          case PerformanceChartV2DataType.RevenueDownloadsRatio30Days:
          case PerformanceChartV2DataType.RevenueDownloadsRatio90Days:
            acc[axisId] = {
              dataFormat: ChartDataFormat.Currency,
              title: `${t('common:revenue_downloads_ratio_days', { days: rollingDays })} (${t('common:apple_ios')}) ${averageLabel()}`,
              shorten: true,
            }
            return acc
          case PerformanceChartV2DataType.RevenueDownloadsRatioAllTime:
            acc[axisId] = {
              dataFormat: ChartDataFormat.Currency,
              title: `${t('common:all_time_revenue_downloads_ratio')} (${t('common:apple_ios')}) ${averageLabel()}`,
              shorten: true,
            }

            return acc
          case PerformanceChartV2DataType.Ranks: {
            const rankBounds = resolveRankAxisBounds(data, dateRange)
            acc[axisId] = {
              dataFormat: ChartDataFormat.Number,
              title: `${t('common:rank')} ${averageLabel()}`,
              reverse: true,
              ...rankBounds,
            }

            return acc
          }
          case PerformanceChartV2DataType.DAU:
            acc[axisId] = {
              dataFormat: ChartDataFormat.Number,
              title: `${t('common:dau')} ${averageLabel()}`,
              shorten: true,
            }

            return acc
          case PerformanceChartV2DataType.MAU:
            acc[axisId] = {
              dataFormat: ChartDataFormat.Number,
              title: t('common:mau'),
              shorten: true,
            }

            return acc
          case PerformanceChartV2DataType.ARPDAU:
            acc[axisId] = {
              dataFormat: ChartDataFormat.Currency,
              title: `${t('live-events:average_revenue_per_daily_active_user')} ${averageLabel()}`,
              shorten: true,
            }

            return acc
          default:
            acc[axisId] = {}
            return acc
        }
      }, {} as { [axisdId: string]: ScaleConfig | {} }),
    },
  }
}

// Resolve fixed min/max bounds for the Ranks Y-axis from the full rank
// dataset so the axis range does not change when individual rank legends
// are toggled off in the chart (GR-2176).
const resolveRankAxisBounds = (data: ReturnType<typeof useGameUpdatePerformanceData>, dateRange?: DateRangeValue): { minY?: number; maxY?: number } => {
  const fromTime = dateRange?.fromDate?.getTime()
  const toTime = dateRange?.toDate?.getTime()
  const ranks: number[] = []
  data.topGrossingRanks?.forEach((rank) => {
    if (valueIsBetween(rank.ts, fromTime, toTime)) ranks.push(Math.round(rank.rank))
  })
  data.freeRanks?.forEach((rank) => {
    if (valueIsBetween(rank.ts, fromTime, toTime)) ranks.push(Math.round(rank.rank))
  })
  if (ranks.length === 0) return {}
  return { minY: Math.min(...ranks), maxY: Math.max(...ranks) }
}

// helper for creating datasets for chart
export const resolveDataset = (
  axisId: string,
  label: string,
  color: string,
  tooltipFormat: ChartDataFormat,
  data: { x: number; y: number }[] = [],
  dateRange?: DateRangeValue,
  spanGaps?: number
) => {
  return {
    yAxisID: axisId,
    label: label,
    fill: false,
    spanGaps: spanGaps ? spanGaps : false,
    tension: 0.1,
    backgroundColor: color,
    borderColor: color,
    data: data.filter((item) => valueIsBetween(item.x, dateRange?.fromDate?.getTime(), dateRange?.toDate?.getTime())),
    tooltipFormat,
  }
}
